import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoanCalculator from './LoanCalculator';
import './LoanCalculatorPage.css';

const LoanCalculatorPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleAuthNav = () => {
    setMobileMenuOpen(false);
    if (isAuthenticated) {
      const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
      navigate(isAdmin ? '/admin' : '/dashboard');
    } else {
      navigate('/auth');
    }
  };

  return (
    <div className="loan-calculator-page">
      {/* Header: desktop = logo + NCR + 24hr + Login; small = logo + hamburger (menu has Login) */}
      <header className="app-header" style={{ background: '#ffffff', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)' }}>
        <div className="header-inner" style={{ maxWidth: '1200px', width: '100%', margin: '0 auto', padding: '0 2rem', height: '64px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }} onClick={() => navigate('/')}>
            <img src="/genlend-logo.png" alt="GenLend" style={{ width: '52px', height: '52px', borderRadius: '50%' }} />
            <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1E3A70', fontFamily: 'var(--gl-font-heading)' }}>GenLend</span>
          </div>

          {/* Desktop nav – hidden on small screens via CSS .header-nav */}
          <nav className="header-nav header-nav-desktop" style={{ alignItems: 'center', gap: '1.5rem' }}>
            <span style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 500 }}>NCR Registered</span>
            <span style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 500 }}>24hr Approval</span>
            <button
              onClick={handleAuthNav}
              style={{ background: 'var(--gl-navy)', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' }}
            >
              {isAuthenticated ? 'My Dashboard' : 'Login'}
            </button>
          </nav>

          {/* Hamburger – visible only on small screens */}
          <button
            type="button"
            className="header-hamburger"
            aria-label="Open menu"
            onClick={() => setMobileMenuOpen(true)}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        </div>

        {/* Mobile menu overlay + panel */}
        {mobileMenuOpen && (
          <>
            <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)} aria-hidden="true" />
            <div className="mobile-menu-panel">
              <div className="mobile-menu-header">
                <span className="mobile-menu-title">Menu</span>
                <button type="button" className="mobile-menu-close" aria-label="Close menu" onClick={() => setMobileMenuOpen(false)}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <nav className="mobile-menu-nav">
                <button type="button" className="mobile-menu-item mobile-menu-login" onClick={handleAuthNav}>
                  {isAuthenticated ? 'My Dashboard' : 'Login'}
                </button>
              </nav>
              <div className="mobile-menu-footer">
                <span className="mobile-menu-trust">NCR Registered</span>
                <span className="mobile-menu-trust">24hr Approval</span>
              </div>
            </div>
          </>
        )}
      </header>

      {/* Brand accent bar — mirrors logo arc colors */}
      <div className="brand-accent-bar"></div>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Bridge Your <span className="highlight">NSFAS</span> Payment Gaps
            </h1>
            <p className="hero-subtitle">
              Get quick cash loans from R200–R750 within 24 hours. Designed specifically for students awaiting NSFAS payments. Repay by the end of the month.
            </p>
            
            <div className="hero-badges">
              <div className="badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <path d="M9 12l2 2 4-4"/>
                </svg>
                NCR Registered
              </div>
              <div className="badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12,6 12,12 16,14"/>
                </svg>
                24-Hour Approval
              </div>
              <div className="badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
                Student-Focused
              </div>
            </div>
          </div>
          
          <div className="hero-calculator">
            <LoanCalculator 
              // onCalculationChange={handleCalculationChange}
              className="hero-calc"
            />
          </div>
        </div>
      </section>

      {/* Wave transition */}
      <div className="wave-divider wave-hero-bottom">
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none">
          <path d="M0,40 C360,80 720,0 1080,40 C1260,60 1380,20 1440,30 L1440,60 L0,60 Z" fill="#ffffff"/>
        </svg>
      </div>

      {/* Student Scenarios Section */}
      <section className="scenarios-section">
        <div className="section-content">
          <h2 className="section-title">We Get It — Life Happens</h2>
          <p className="section-subtitle">GenLend is here for those moments when you need a little extra before your NSFAS payment comes through</p>
          <div className="scenarios-grid">
            <div className="scenario-card">
              <div className="scenario-icon scenario-icon--teal">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.4-.1.9.3 1.1l5.6 3.4-3.6 3.6-2.3-.8c-.4-.1-.8 0-1 .3l-.3.5c-.2.3-.1.7.2.9l3.4 2.4 2.4 3.4c.2.3.6.4.9.2l.5-.3c.3-.2.4-.6.3-1l-.8-2.3 3.6-3.6 3.4 5.6c.2.4.7.5 1.1.3l.5-.3c.4-.2.6-.6.5-1.1z"/>
                </svg>
              </div>
              <h3 className="scenario-title">Unplanned Travel</h3>
              <p className="scenario-desc">Need to get home for a family emergency or can't afford that bus fare back to campus? We've got you.</p>
            </div>
            <div className="scenario-card">
              <div className="scenario-icon scenario-icon--red">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                </svg>
              </div>
              <h3 className="scenario-title">Medical Costs</h3>
              <p className="scenario-desc">Unexpected doctor visit or pharmacy run? Don't let health costs catch you off guard while you wait for NSFAS.</p>
            </div>
            <div className="scenario-card">
              <div className="scenario-icon scenario-icon--green">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                  <path d="M6 12v5c3 3 9 3 12 0v-5"/>
                </svg>
              </div>
              <h3 className="scenario-title">Varsity Costs</h3>
              <p className="scenario-desc">Textbooks, printing, lab materials — the little academic expenses that add up fast. Keep studying, we'll bridge the gap.</p>
            </div>
            <div className="scenario-card">
              <div className="scenario-icon scenario-icon--yellow">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                  <line x1="3" y1="6" x2="21" y2="6"/>
                  <path d="M16 10a4 4 0 0 1-8 0"/>
                </svg>
              </div>
              <h3 className="scenario-title">Groceries &amp; Essentials</h3>
              <p className="scenario-desc">Fridge empty and payday is still weeks away? Get what you need now, repay when your NSFAS comes through.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Repayment Info Section */}
      <section className="repayment-info-section">
        <div className="section-content">
          <div className="repayment-info-card">
            <div className="repayment-info-left">
              <h2 className="repayment-info-title">Flexible Repayment Over 90 Days</h2>
              <p className="repayment-info-desc">
                We understand student finances. The loan is repaid by the <strong>end of the month</strong> it is paid out, and the cost is worked out on the days left in that month. No pressure, no stress.
              </p>
              <ul className="repayment-highlights">
                <li>Up to 90-day repayment period</li>
                <li>Aligned with NSFAS payment schedule</li>
                <li>All fees disclosed upfront — no surprises</li>
                <li>NCR-registered and fully compliant</li>
              </ul>
            </div>
            <div className="repayment-info-right">
              <div className="repayment-badge-large">90 Days</div>
              <span className="repayment-badge-label">Maximum Repayment Period</span>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="section-content">
          <h2 className="section-title">How It Works</h2>
          <p className="section-subtitle">Get your loan in 3 simple steps</p>
          <div className="steps-grid">
            <div className="step-item">
              <div className="step-number step-number--teal">1</div>
              <h3 className="step-title">Apply Online</h3>
              <p className="step-description">
                Fill out our quick application form with your student details
              </p>
            </div>
            <div className="step-item">
              <div className="step-number step-number--green">2</div>
              <h3 className="step-title">Get Approved</h3>
              <p className="step-description">
                Receive approval within 24 hours via SMS and email
              </p>
            </div>
            <div className="step-item">
              <div className="step-number step-number--navy">3</div>
              <h3 className="step-title">Receive Funds</h3>
              <p className="step-description">
                Money is transferred directly to your bank account
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted by Students Section */}
      <section className="trusted-section">
        <div className="section-content">
          <h2 className="section-title">Trusted by Students Nationwide</h2>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">5,000+</div>
              <div className="stat-label">Students Helped</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">24hrs</div>
              <div className="stat-label">Average Approval</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">98%</div>
              <div className="stat-label">Satisfaction Rate</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">R2M+</div>
              <div className="stat-label">Loans Disbursed</div>
            </div>
          </div>
        </div>
      </section>

      {/* Wave transition into CTA */}
      <div className="wave-divider wave-cta-top">
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none">
          <path d="M0,20 C240,60 480,0 720,30 C960,60 1200,10 1440,40 L1440,60 L0,60 Z" fill="#1E3A70"/>
        </svg>
      </div>

      {/* Call to Action Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2 className="cta-title">Ready to Bridge Your Payment Gap?</h2>
          <p className="cta-subtitle">Join thousands of students who trust GenLend for quick financial relief</p>
          <button
            className="cta-button"
            onClick={() => navigate(isAuthenticated ? '/apply' : '/auth')}
          >
            {isAuthenticated ? 'Continue Application' : 'Start Your Application'}
          </button>
          <button className="cta-button-secondary" onClick={() => navigate('/faq')}>
            Read Our FAQ
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="main-footer">
        <div className="footer-content">
          <div className="footer-section">
            <div className="footer-logo">
              <div className="logo-container">
                <img src="/genlend-logo.png" alt="GenLend" style={{ width: '46px', height: '46px', borderRadius: '50%' }} />
                <span className="logo-text">GenLend</span>
              </div>
              <p className="footer-tagline">Bridging the gap between students and financial freedom.</p>
            </div>
          </div>
          
          <div className="footer-section">
            <h4 className="footer-title">Legal</h4>
            <div className="contact-info">
              <div className="contact-item">
                <a href="/terms">Terms & Conditions</a>
              </div>
              <div className="contact-item">
                <a href="/privacy">Privacy Policy</a>
              </div>
              <div className="contact-item">
                <a href="/ncr">NCR Registration</a>
              </div>
            </div>
          </div>
          
          <div className="footer-section">
            <h4 className="footer-title">Support</h4>
            <div className="contact-info">
              <div className="contact-item">
                <a href="/contact">Contact Us</a>
              </div>
              <div className="contact-item">
                <a href="/faq">FAQ</a>
              </div>
              <div className="contact-item">
                <a href="/help">Help Center</a>
              </div>
            </div>
          </div>
          
          <div className="footer-section">
            <h4 className="footer-title">Contact</h4>
            <div className="contact-info">
              <div className="contact-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                <a href="https://wa.me/27722734008" target="_blank" rel="noopener noreferrer">WhatsApp 072 273 4008</a>
              </div>
              <div className="contact-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <a href="mailto:info@genlend.co.za">info@genlend.co.za</a>
              </div>
            </div>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p className="copyright">© 2026 GenLend. All rights reserved. NCR Registered Credit Provider.</p>
          <button className="help-button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
              <path d="M12 17h.01"/>
            </svg>
          </button>
        </div>
      </footer>
    </div>
  );
};

export default LoanCalculatorPage;

